import { ChevronLeft, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { GuideAdvanceButtonPos } from "@/lib/guide/types";

// Position → fixed placement classes. Default is bottom-center; a step can
// override (`advanceButtonPos`) when its spotlight sits there too.
const POSITION_CLASS: Record<GuideAdvanceButtonPos, string> = {
  "bottom-center": "bottom-8 left-1/2 -translate-x-1/2",
  "bottom-right": "bottom-24 right-8",
  "top-center": "top-24 left-1/2 -translate-x-1/2",
};

const PILL =
  "pointer-events-auto rounded-full text-sm font-semibold shadow-lg shadow-black/20 backdrop-blur-sm";

/**
 * Fixed control row for an anchored step: the exit
 * (round ✕) pinned far-LEFT, away from the grouped Previous/Next nav on the
 * RIGHT, so an exit misclick can't drop the guide. Pinned to one screen spot
 * across steps (per-step overridable). The step's DESCRIPTION is NOT here — it
 * rides in `GuideCaption`, anchored to the spotlit element (above/below by
 * available space). On an ACTION step the advance button is DISABLED and reads
 * "try it yourself"; on an explain/last step it's the live "Suivant"/"Terminé".
 * Previous is greyed on the first step.
 */
export function GuideAdvanceButton({
  label,
  onNext,
  nextDisabled,
  position,
  onLeave,
  onPrev,
  prevDisabled,
}: Readonly<{
  label: string;
  onNext: () => void;
  /** Advance is disabled on action steps (the user does the real thing). */
  nextDisabled: boolean;
  position: GuideAdvanceButtonPos;
  /** Explicit dismiss — the "Leave" button. */
  onLeave?: () => void;
  onPrev: () => void;
  /** No step before the first — Previous is greyed there. */
  prevDisabled: boolean;
}>) {
  const { t } = useTranslation();
  return (
    <div
      className={`pointer-events-none fixed z-[10060] flex w-[300px] items-center justify-between gap-2 ${POSITION_CLASS[position]}`}
    >
      {onLeave ? (
        <button
          type="button"
          data-testid="guide-leave-button"
          onClick={onLeave}
          aria-label={t("guide.leave")}
          title={t("guide.leave")}
          className="pointer-events-auto flex h-10 w-10 items-center justify-center rounded-full bg-background/85 text-muted-foreground shadow-lg shadow-black/20 ring-1 ring-border backdrop-blur-sm hover:bg-background hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      ) : (
        <span />
      )}
      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid="guide-prev-button"
          onClick={onPrev}
          disabled={prevDisabled}
          aria-disabled={prevDisabled}
          className={`${PILL} flex items-center gap-1 px-4 py-2.5 ${
            prevDisabled
              ? "cursor-default bg-background/70 text-muted-foreground/60 ring-1 ring-border"
              : "bg-background/80 text-guide ring-1 ring-guide/40 hover:bg-background"
          }`}
        >
          <ChevronLeft className="h-4 w-4" />
          {t("guide.previous")}
        </button>
        <button
          type="button"
          data-testid="guide-advance-button"
          onClick={onNext}
          disabled={nextDisabled}
          aria-disabled={nextDisabled}
          className={`${PILL} px-6 py-2.5 ${
            nextDisabled
              ? "cursor-default bg-background/70 text-guide ring-1 ring-guide/40"
              : "bg-guide text-guide-foreground hover:bg-guide/90"
          }`}
        >
          {label}
        </button>
      </div>
    </div>
  );
}
